/**
 * Web Conversation Page
 * 
 * Clean composition layer using the Facade pattern.
 * 
 * Architecture:
 * - Uses shared useConversationPageFacade from @ideoz/core (100% reusable!)
 * - Injects web-specific implementations (navigation, auth)
 * - Passes organized facade to ConversationPageTemplate
 * 
 * Benefits:
 * - Single source of truth for conversation logic
 * - Easy to test (mock one facade)
 * - Platform-agnostic template
 * - Consistent pattern with LandingPage
 */

import React, { useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ConversationPageTemplate, showToast } from '@ideoz/ui';
import { useConversationPageFacade, useAppDispatch, getUserProfile, getAccessToken } from '@ideoz/core';
import { useWebNavigation } from '../hooks/useWebNavigation';
import { useAuthDialog } from '../hooks/useAuthDialog';

export default function ConversationPage(): JSX.Element {
  // ========== Get URL Parameters ==========
  const { id: urlConversationId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  
  // Get mode from URL parameters (for plugin redirects)
  const urlMode = searchParams.get('mode') as 'prototyper' | 'uncover' | null;

  // ========== Platform-Specific Implementations ==========
  const navigation = useWebNavigation();
  const authDialog = useAuthDialog();
  
  // Callback to refresh user profile (to update credit balance after prototype creation)
  const handleRefreshUserProfile = useCallback(async () => {
    const accessToken = getAccessToken();
    if (accessToken) {
      await dispatch(getUserProfile(accessToken)).unwrap();
    }
  }, [dispatch]);
  
  // Adapt authDialog to AuthFacade interface
  const auth = React.useMemo(() => ({
    isOpen: authDialog.isOpen,
    mode: authDialog.mode,
    isLoading: authDialog.isLoading,
    openLogin: authDialog.openLogin,
    openRegister: authDialog.openRegister,
    close: authDialog.closeDialog,
  }), [authDialog]);

  // ========== Shared Facade (100% Reusable) ==========
  const facade = useConversationPageFacade({
    navigation,
    auth,
    showToast,
    platform: 'web',
    urlConversationId: urlConversationId || null,
    initialMode: urlMode || undefined, // Pass URL mode to facade for initialization
    onRefreshUserProfile: handleRefreshUserProfile,
  });

  return (
    <ConversationPageTemplate
      facade={facade}
      
      // Web-specific overrides
      authDialogState={authDialog}
      isWeb={true}
      urlConversationId={urlConversationId || null}
    />
  );
}
